import { useTranslation } from 'react-i18next'

import { FLOATING_ELEMENT_PLACEMENT } from '../../../../constants/common'
import { getQaSelector } from '../../../../utils/common'
import Button, { BUTTON_HEIGHT_32 } from '../../../Button'
import { PageEmptyState } from '../../../PageEmptyState'
import Text from '../../../Text'
import { useApplicantProfileContext } from '../RentalPortalApplicantProfile.hooks'

import { ApplicantProfileComplianceBlock } from './ApplicantProfileComplianceBlock'
import {
  QA_NAME_APPLICANT_PROFILE_COMPLIANCE,
  QA_NAME_APPLICANT_PROFILE_COMPLIANCE_HANDOFF,
  QA_NAME_APPLICANT_PROFILE_COMPLIANCE_RELOAD,
  QA_NAME_APPLICANT_PROFILE_COMPLIANCE_RETRY,
  QA_NAME_APPLICANT_PROFILE_COMPLIANCE_SCREENING,
} from './RentalPortalApplicantProfileCompliance.constants'
import {
  useApplicantProfileCompliance,
  useApplicantProfileHandoffRetry,
} from './RentalPortalApplicantProfileCompliance.hooks'
import {
  getApplicantComplianceState,
  getApplicantHandoffBlock,
  getApplicantHandoffRetryAction,
  getApplicantScreeningBlock,
  getHasApplicantComplianceHandoff,
  getHasApplicantComplianceScreening,
  getIsApplicantComplianceError,
  getIsApplicantComplianceLoading,
} from './RentalPortalApplicantProfileCompliance.utils'

import * as styles from './RentalPortalApplicantProfileCompliance.module.css'

import RefreshIcon from '../../../../icons/refresh.svg'
import RentalPortalIcon from '../../../../icons/rental-portal.svg'

function RentalPortalApplicantProfileCompliance() {
  const { t } = useTranslation()

  const { applicantData, isInitiated } = useApplicantProfileContext()

  const {
    handleRefresh,
    handleReload,
    handoff,
    hasError,
    isInitiated: isComplianceInitiated,
    isLoading,
    screening,
  } = useApplicantProfileCompliance({ applicantId: applicantData?.id })

  const { handleClickRetry, isSubmitting } = useApplicantProfileHandoffRetry({
    handoff,
    onReload: handleReload,
  })

  const state = getApplicantComplianceState({
    hasError,
    isInitiated: isInitiated && isComplianceInitiated,
  })

  const retryAction = getApplicantHandoffRetryAction({ handoff, t })

  return (
    <div
      {...getQaSelector({ value: QA_NAME_APPLICANT_PROFILE_COMPLIANCE })}
      className={styles.RentalPortalApplicantProfileCompliance}
    >
      {getIsApplicantComplianceError(state) ? (
        <PageEmptyState EntityIcon={RentalPortalIcon}>
          <Text isSecondary>
            {t('RentalPortal.ApplicantProfileComplianceLoadErrorKey')}
          </Text>
          <Button.Flat
            beforeIconProps={{ Icon: RefreshIcon }}
            isLoading={isLoading}
            qaName={QA_NAME_APPLICANT_PROFILE_COMPLIANCE_RELOAD}
            text={t('RentalPortal.ListingRetryBtnKey')}
            onClick={handleRefresh}
          />
        </PageEmptyState>
      ) : (
        <>
          {getHasApplicantComplianceScreening(applicantData) ? (
            <ApplicantProfileComplianceBlock
              {...getApplicantScreeningBlock({ screening, t })}
              isSkeleton={getIsApplicantComplianceLoading(state)}
              qaName={QA_NAME_APPLICANT_PROFILE_COMPLIANCE_SCREENING}
              titleKey='RentalPortal.ApplicantProfileComplianceScreeningTitleKey'
            />
          ) : null}
          {getHasApplicantComplianceHandoff(applicantData) ? (
            <ApplicantProfileComplianceBlock
              {...getApplicantHandoffBlock({ handoff, t })}
              isSkeleton={getIsApplicantComplianceLoading(state)}
              qaName={QA_NAME_APPLICANT_PROFILE_COMPLIANCE_HANDOFF}
              titleKey='RentalPortal.ApplicantProfileComplianceHandoffTitleKey'
            >
              {retryAction ? (
                <Button.Outlined
                  isGrey
                  beforeIconProps={{ Icon: RefreshIcon }}
                  disabled={Boolean(retryAction.tooltip)}
                  height={BUTTON_HEIGHT_32}
                  isLoading={isSubmitting}
                  qaName={QA_NAME_APPLICANT_PROFILE_COMPLIANCE_RETRY}
                  text={t('RentalPortal.ApplicantComplianceHandoffRetryBtnKey')}
                  tooltip={retryAction.tooltip}
                  tooltipPlacement={FLOATING_ELEMENT_PLACEMENT.top}
                  type='button'
                  onClick={handleClickRetry}
                />
              ) : null}
            </ApplicantProfileComplianceBlock>
          ) : null}
        </>
      )}
    </div>
  )
}

export default RentalPortalApplicantProfileCompliance
